import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import { TextV2 } from '@insights-ltd/design-library/components';
import CancelIcon from '@mui/icons-material/Cancel';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { Evaluator, ProfileType } from 'types/types';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { PurchaseErrorReason } from './helper';
import { RemoveButton } from './RemoveButton';

const ERROR_MESSAGE_KEYS: Record<PurchaseErrorReason, string> = {
  IDTL_NEEDS_DISCOVERY:
    'ui.event-management.learners.purchase-profiles.idtl-purchase.needs-discovery',
  IDTL_ALREADY_EXISTS:
    'ui.event-management.learners.purchase-profiles.idtl-purchase.already-has-idtl',
  PIP_NEEDS_DISCOVERY:
    'ui.event-management.learners.purchase-profiles.idtl-purchase.needs-discovery',
  PIP_RETIRING_EVALUATOR:
    'ui.event-management.learners.purchase-profiles.pip-purchase.retiring-evaluator',
};

const ARCHIVED_PROFILE_TYPE_TEXT_KEYS: Partial<Record<ProfileType, string>> = {
  DISCOVERY_PROFILE: 'ui.event-management.product-types.insights-discovery',
  IMPACT_PROFILE: 'ui.event-management.product-types.insights-personal-impact',
};

const isPIPErrorReason = (reason?: PurchaseErrorReason) =>
  reason === 'PIP_NEEDS_DISCOVERY' || reason === 'PIP_RETIRING_EVALUATOR';

export const LearnerErrorList = ({
  learnerId,
  organisationId,
  evaluatorLinkOrganisationId,
  evaluatorLinkId,
  fullName,
  primaryEmail,
  removeCheckedItem,
  latestEvaluationId,
  latestEvaluationType,
  errorReason,
  archivedProfileType,
}: {
  learnerId?: string;
  organisationId?: string;
  evaluatorLinkOrganisationId?: string;
  evaluatorLinkId?: string;
  fullName: string;
  primaryEmail: string;
  removeCheckedItem?: (id: string) => void;
  latestEvaluationId: string;
  latestEvaluationType: Evaluator;
  errorReason?: PurchaseErrorReason;
  archivedProfileType?: ProfileType;
}) => {
  const { t } = useTranslation();

  const learnerProfileLink = evaluatorLinkId
    ? `/evaluator-links/${evaluatorLinkId}/${evaluatorLinkOrganisationId}/${learnerId}`
    : `/learners/${organisationId}/${learnerId}`;

  const errorMessage = errorReason ? t(ERROR_MESSAGE_KEYS[errorReason]) : '';

  return (
    <Grid
      container
      sx={[
        {
          alignItems: 'flex-start',
        },
        (theme) => ({
          backgroundColor: '#F4F6F7',
          padding: '12px 16px',
          minHeight: '84px',
          borderRadius: '4px',
          wordWrap: 'break-word',
          marginBottom: theme.spacing(spacingSizeMap.XS),
        }),
      ]}
    >
      <Grid
        sx={() => ({
          '& > p': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          paddingTop: `0 !important`,
        })}
        size={3}
      >
        <TextV2 component="p" variant="bodyBold">
          {fullName}
        </TextV2>
        <TextV2 component="p" color="textSecondary">
          {primaryEmail}
        </TextV2>
      </Grid>
      <Grid sx={{ paddingTop: `0 !important` }} size={1}>
        <RemoveButton
          removeCheckedItem={removeCheckedItem}
          latestEvaluationId={latestEvaluationId}
          t={t}
        />
      </Grid>
      <Grid size={8}>
        {(isPIPErrorReason(errorReason) ||
          latestEvaluationType !== 'CLASSIC_EXPLORE') && (
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Grid>
              <CancelIcon
                color="error"
                sx={{
                  marginTop: '2px',
                }}
              />
            </Grid>
            <Grid size="grow">
              {archivedProfileType ? (
                <TextV2
                  component="p"
                  color="textPrimary"
                  variant="bodyBold"
                  data-testid="archived-profile-error-message"
                >
                  <Trans
                    i18nKey={
                      'ui.event-management.learners.purchase-profiles.your-basket.archived-profile-type'
                    }
                    values={{
                      profileType: t(
                        ARCHIVED_PROFILE_TYPE_TEXT_KEYS[archivedProfileType] ??
                          '',
                      ),
                    }}
                  >
                    <Link component={RouterLink} to={learnerProfileLink} />
                  </Trans>
                </TextV2>
              ) : (
                <TextV2
                  component="p"
                  color="textPrimary"
                  variant="bodyBold"
                  data-testid="error-message"
                  sx={{
                    overflowWrap: 'break-word',
                  }}
                >
                  {errorMessage}
                </TextV2>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
